import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	BellIcon,
	Building2Icon,
	CreditCardIcon,
	ShieldIcon,
	UserIcon,
	UsersIcon,
	type LucideIcon,
} from "lucide-react";
import { WorkspaceSettings } from "./WorkspaceSettings.js";
import { cn } from "./cn.js";
import type { OnboardingSession } from "./onboarding-flow.js";
import {
	SETTINGS_GROUPS,
	SETTINGS_TABS,
	type NotificationPrefs,
	type RiskConfig,
	type SettingsTab,
} from "./settings.js";
import type { ThemePreference } from "./theme.js";

const SETTINGS_ICONS: Record<SettingsTab, LucideIcon> = {
	account: UserIcon,
	notifications: BellIcon,
	security: ShieldIcon,
	workspace: Building2Icon,
	members: UsersIcon,
	billing: CreditCardIcon,
};

type Props = {
	open: boolean;
	tab: SettingsTab;
	onTabChange: (tab: SettingsTab) => void;
	onOpenChange: (open: boolean) => void;
	session: OnboardingSession;
	risk: RiskConfig;
	notifications: NotificationPrefs;
	themePref: ThemePreference;
	onCycleTheme: () => void;
	onSaveAccount: (patch: {
		firstName: string;
		lastName: string;
		email: string;
	}) => void;
	onSaveWorkspace: (patch: { company: string; website: string }) => void;
	onSaveRisk: (risk: RiskConfig) => void;
	onSaveNotifications: (prefs: NotificationPrefs) => void;
};

/** Settings as an overlay — desk stays underneath. */
export function SettingsModal({
	open,
	tab,
	onTabChange,
	onOpenChange,
	session,
	risk,
	notifications,
	themePref,
	onCycleTheme,
	onSaveAccount,
	onSaveWorkspace,
	onSaveRisk,
	onSaveNotifications,
}: Props) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton
				className="settings-modal gap-0 overflow-hidden p-0"
				overlayClassName="settings-modal-overlay"
			>
				<DialogTitle className="sr-only">Settings</DialogTitle>
				<DialogDescription className="sr-only">
					Account, workspace, members, and billing.
				</DialogDescription>
				<div className="settings-layout">
					<nav className="settings-nav" aria-label="Settings">
						{SETTINGS_GROUPS.map((group) => (
							<div key={group.id} className="settings-nav-group">
								<p className="settings-nav-group-label">{group.label}</p>
								{SETTINGS_TABS.filter((item) => item.group === group.id).map(
									(item) => {
										const Icon = SETTINGS_ICONS[item.id];
										return (
											<button
												key={item.id}
												type="button"
												className={cn(
													"settings-nav-item",
													tab === item.id && "settings-nav-item--active",
												)}
												onClick={() => onTabChange(item.id)}
											>
												<Icon className="settings-nav-icon" aria-hidden />
												<span className="settings-nav-label">
													{item.label}
												</span>
											</button>
										);
									},
								)}
							</div>
						))}
					</nav>
					<div className="settings-main">
						<WorkspaceSettings
							tab={tab}
							session={session}
							risk={risk}
							notifications={notifications}
							themePref={themePref}
							onCycleTheme={onCycleTheme}
							onSaveAccount={onSaveAccount}
							onSaveWorkspace={onSaveWorkspace}
							onSaveRisk={onSaveRisk}
							onSaveNotifications={onSaveNotifications}
						/>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
